import { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import LoginScreen from './components/Login';
import AuthCallback from './components/AuthCallback';
import Dashboard from './components/Dashboard';
import Weekly from './components/Weekly';
import Transactions from './components/Transactions';
import Settings from './components/Settings';
import Advisor from './components/Advisor';
import Onboarding from './components/Onboarding';
import { getHousehold, syncPlaid, syncGoogleSheets } from './api/client';
import Privacy from './components/Privacy';
import PlaidOAuthReturn from './components/PlaidOAuthReturn';
import Accounts from './components/Accounts';
import MobileLayout from './components/layout/MobileLayout';
import DesktopLayout from './components/layout/DesktopLayout';
import Planning from './components/analytics/Planning';
import MonthlyDeepDive from './components/analytics/MonthlyDeepDive';
import Trends from './components/analytics/Trends';
import YearOverview from './components/analytics/YearOverview';

const AppContent = () => {
  const { user, loading, appMode } = useAuth();
  const { COLORS, themeKey } = useTheme();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [householdName, setHouseholdName]   = useState('');
  const [hhChecked, setHhChecked]           = useState(false);
  const [advisorMessages, setAdvisorMessages] = useState([]);

  // Sync theme-color meta tag with active theme
  useEffect(() => {
    const meta = document.getElementById('theme-color-meta');
    if (meta) meta.setAttribute('content', COLORS.navBg || COLORS.bg);
  }, [themeKey, COLORS]);

  useEffect(() => {
    if (!user) return;
    getHousehold()
      .then(res => {
        const hh = res.data?.data || res.data;
        setHouseholdName(hh?.name || '');
        if (hh && !hh.onboarding_complete) setShowOnboarding(true);
        // Autosync on load — backend will skip if synced within last 30 min
        syncPlaid(false).catch(() => {});
        syncGoogleSheets(false).catch(() => {});
      })
      .catch(() => {})
      .finally(() => setHhChecked(true));
  }, [user]);

  if (loading || (user && !hhChecked)) return (
    <div style={{ minHeight: '100vh', background: COLORS.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: COLORS.textMuted, fontSize: 16 }}>Loading...</div>
    </div>
  );

  // Real browser paths (not hash routes) — handled before the HashRouter.
  if (window.location.pathname === '/auth/callback') {
    return <AuthCallback onSuccess={() => { window.location.hash = '/dashboard'; }} />;
  }
  if (window.location.pathname === '/plaid/oauth') {
    return <PlaidOAuthReturn />;
  }
  if (window.location.pathname === '/privacy') {
    return <Privacy />;
  }

  if (!user) return <LoginScreen />;

  if (showOnboarding) return (
    <Onboarding
      householdName={householdName}
      onComplete={() => setShowOnboarding(false)}
    />
  );

  // The mode decides the whole chrome: Simple → bottom-nav mobile, Analytical → sidebar desktop.
  const Layout = appMode === 'analytical' ? DesktopLayout : MobileLayout;

  return (
    <HashRouter>
      <Layout>
        <Routes>
          <Route path="/dashboard"    element={<Dashboard />} />
          <Route path="/weekly"       element={<Weekly />} />
          <Route path="/transactions" element={<Transactions />} />
          <Route path="/accounts"     element={<Accounts />} />
          <Route path="/advisor"      element={<Advisor messages={advisorMessages} setMessages={setAdvisorMessages} />} />
          <Route path="/settings"     element={<Settings />} />
          {/* Analytical-only pages */}
          <Route path="/analytics/overview" element={<YearOverview />} />
          <Route path="/analytics/monthly"  element={<MonthlyDeepDive />} />
          <Route path="/analytics/forecast" element={<Planning />} />
          <Route path="/analytics/trends"   element={<Trends />} />
          {/* Anything else (including the initial empty hash) → dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Layout>
    </HashRouter>
  );
};

const App = () => (
  <ThemeProvider>
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  </ThemeProvider>
);

export default App;
